(function () {
  const LISA_CART_PARAM = 'lisa_gwp';

  // Tracks variant IDs currently being added to prevent duplicate adds during async operations
  const pendingGiftVariantIds = new Set();

  // Debounced function for GWP logic
  const debouncedGWPCheck = debounce(() => {
    getCartJson().then((cartData) => {
      lisaAddRemoveGWPItemsHandler(cartData);
    });
  }, 500);

  function cartContainsDiscountCode(cartData, discountCode) {
    if (!discountCode) return true;
    const requiredCodes = discountCode.split(',').map((c) => c.trim().toUpperCase()).filter(Boolean);
    const cartCodes = (cartData.discount_codes || [])
      .filter((dc) => dc.applicable)
      .map((dc) => dc.code.toUpperCase());
    return requiredCodes.some((code) => cartCodes.includes(code));
  }

  function cartItemsContainsCollection(cartData, giftCollectionsOnly) {
    // If no collection restrictions, always return true
    if (!giftCollectionsOnly || giftCollectionsOnly.length === 0) {
      return true;
    }

    // Check if any cart item belongs to the specified collections
    for (const item of cartData.items) {
      // Get collections for this cart item from the DOM
      const collectionScript = document.querySelector(`script[data-cart-item-collections][data-key="${item.key}"]`);
      if (collectionScript) {
        const itemCollections = JSON.parse(collectionScript.textContent || '[]').map(Number);

        // Check for intersection between itemCollections and giftCollectionsOnly
        for (const giftCollectionId of giftCollectionsOnly) {
          if (itemCollections.includes(Number(giftCollectionId))) {
            return true;
          }
        }
      }
    }

    // No matching collections found
    return false;
  }

  /* -----------------------
   MAIN HANDLER
-------------------------*/
  function lisaAddRemoveGWPItemsHandler(cartData) {
    const gifts = window.themeConfig.gifts;
    const gifts_to_add = [];
    const gifts_to_remove = [];
    const cartTotal = cartData.total_price / 100;
    const totalItems = cartData.item_count;
    const allProductsAreAddons = cartData.items.every((item) => {
      return String(item.product_type).toLowerCase() === 'avis-add-charge';
    });

    gifts.forEach((gift) => {
      const giftInCart = cartData.items.find((item) => item.id === gift.variant_id);

      const minimum = parseFloat(gift.minimum);
      const maximum = parseFloat(gift.maximum);

      // Add gift if eligible
      if (
        !giftInCart &&
        !pendingGiftVariantIds.has(gift.variant_id) &&
        totalItems > 0 &&
        !allProductsAreAddons &&
        cartTotal >= minimum &&
        cartTotal <= maximum &&
        cartItemsContainsCollection(cartData, gift.collections_only) &&
        cartContainsDiscountCode(cartData, gift.discount_code)
      ) {
        gifts_to_add.push({
          id: gift.variant_id,
          quantity: 1,
          properties: { 
            _lisa_gift: 'true',
            _lisa_gift_line_message: gift.discount_name_message,
            _lisa_gift_discount_percentage: gift.discount_amount_percentage.toString(),
            _lisa_gift_market: gift.market_handle || 'all',
            _lisa_gift_minimum: minimum.toString(),
            _lisa_gift_maximum: maximum.toString(),
            _lisa_gift_collections_only: gift.collections_only ? gift.collections_only.join(',') : '',
            _lisa_gift_discount_code: gift.discount_code || '',
          },
        });
      }
    });

    // Remove gifts that are no longer eligible 
    cartData.items.forEach((item) => {
      const giftConfig = gifts.find((g) => g.variant_id === item.id);
      if (giftConfig) {
        const minimum = parseFloat(giftConfig.minimum);
        const maximum = parseFloat(giftConfig.maximum);
        const outOfRange = cartTotal < minimum || cartTotal > maximum;
        const discountCodeMissing = giftConfig.discount_code && !cartContainsDiscountCode(cartData, giftConfig.discount_code);
        if (!cartItemsContainsCollection(cartData, giftConfig.collections_only) || outOfRange || discountCodeMissing) {
          gifts_to_remove.push(item.key);
        }
      } else if (item.properties?._lisa_gift === 'true') {
        // Gift item in cart but no longer present in config
        gifts_to_remove.push(item.key);
      }
    });

    if (gifts_to_add.length || gifts_to_remove.length) {
      addRemoveGiftItemsToCart(gifts_to_add, gifts_to_remove);
    }
  }

  /* -----------------------
   CART MODIFICATION
-------------------------*/
  function addRemoveGiftItemsToCart(gifts_to_add = [], gifts_to_remove = []) {
    // Mark gifts as pending immediately to block duplicate adds from concurrent checks
    gifts_to_add.forEach((g) => pendingGiftVariantIds.add(g.id));

    const removePromise = gifts_to_remove.length
      ? (() => {
          const updates = {};
          gifts_to_remove.forEach((key) => (updates[key] = 0));

          return fetch(routes.cart_update_url + '?' + LISA_CART_PARAM, {
            ...fetchConfig(),
            body: JSON.stringify({ updates }),
          })
            .then((res) => res.json())
            .then(() => publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gwp', action: 'remove' }));
        })()
      : Promise.resolve();

    removePromise.then(() => {
      if (!gifts_to_add.length) return;

      fetch(routes.cart_add_url + '?' + LISA_CART_PARAM, {
        ...fetchConfig(),
        body: JSON.stringify({ items: gifts_to_add }),
      })
        .then((res) => res.json())
        .then(() => {
          gifts_to_add.forEach((g) => pendingGiftVariantIds.delete(g.id));
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gwp', action: 'add' });
        })
        .catch(() => {
          gifts_to_add.forEach((g) => pendingGiftVariantIds.delete(g.id));
        });
    });
  }

  /* -----------------------
   TRIGGER GWP ON CART EVENTS
-------------------------*/
  function lisaAddRemoveGWPItems(e) {

    if (!e?.url) return;

    // Bail early if triggered by GWP logic itself
    if (e.url.indexOf(LISA_CART_PARAM) !== -1) {
      return;
    }

    debouncedGWPCheck();
  }

  /* -----------------------
   Listen to interceptor events
-------------------------*/
  if (window.themeConfig.gifts.length > 0) {
    subscribe(PUB_SUB_EVENTS.cartInterceptor.add, lisaAddRemoveGWPItems);
    subscribe(PUB_SUB_EVENTS.cartInterceptor.update, lisaAddRemoveGWPItems);
    subscribe(PUB_SUB_EVENTS.cartInterceptor.change, lisaAddRemoveGWPItems);
    subscribe(PUB_SUB_EVENTS.cartInterceptor.clear, lisaAddRemoveGWPItems);
  }
})();
